import { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import styles from './phone-model-select.module.scss';

export default function PhoneModelSelect({ value, onChange, label = 'Phone Model' }) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const options = [
    { label: 'iPhone', value: 'iphone' },
    { label: 'Android', value: 'android' }
  ];

  const selectedIndex = options.findIndex(o => o.value === value);
  const [activeIndex, setActiveIndex] = useState(selectedIndex >= 0 ? selectedIndex : 0);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (buttonRef.current?.contains(e.target) || listRef.current?.contains(e.target)) return;
      close();
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open, close]);

  useEffect(() => {
    if (selectedIndex >= 0) setActiveIndex(selectedIndex);
  }, [selectedIndex]);

  const commit = (index) => {
    const opt = options[index];
    if (!opt) return;
    onChange(opt.value);
    setActiveIndex(index);
    close();
    buttonRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (!open && ['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (open) {
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex(i => (i + 1) % options.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex(i => (i - 1 + options.length) % options.length);
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        commit(activeIndex);
      }
    }
  };

  const wrapperClass = [styles.phoneModelSelectWrapper, open ? styles.isOpen : ''].join(' ');

  return (
    <div className={wrapperClass}>
      <button
        type="button"
        className={styles.phoneModelSelectButton}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen(o => !o)}
        onKeyDown={onKeyDown}
        ref={buttonRef}
      >
        <span className={value ? styles.phoneModelValue : styles.phoneModelPlaceholder}>
          {value ? options.find(o => o.value === value)?.label : 'Select your phone model'}
        </span>
      </button>
      {open && (
        <ul
          role="listbox"
          aria-label={label}
          className={styles.phoneModelOptions}
          ref={listRef}
        >
          {options.map((opt, i) => (
            <li
              role="option"
              key={opt.value}
              aria-selected={value === opt.value}
              className={[
                styles.phoneModelOption,
                i === activeIndex ? styles.isActive : '',
                value === opt.value ? styles.isSelected : ''
              ].join(' ')}
              onMouseDown={(e) => { e.preventDefault(); commit(i); }}
              onMouseEnter={() => setActiveIndex(i)}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

PhoneModelSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string
};
